<script setup lang="ts">
  import EmployeeAutoComplete from '@/components/company/EmployeeAutoComplete.vue';
  import SelectCalendar from '@/components/date-time/SelectCalendar.vue';
  import { EmployeeWithOrganizationDto } from '@/services/api/employee-api.ts';
  import {
    AttendanceItem,
    getAttendanceSearchItemAsync,
  } from '@/services/attendance-svc.ts';
  import { loadWithStatusAndErrorHandleAsync } from '@/services/loading-svc.ts';
  import dayjs from 'dayjs';
  import { ref } from 'vue';

  const loading = ref<boolean>(false);
  const date = ref<dayjs.Dayjs>(dayjs());
  const searchDate = ref<string>();
  const employees = ref<EmployeeWithOrganizationDto[]>([]);
  const attendanceItems = ref<AttendanceItem[]>();

  function onChangeEmployees(empList: EmployeeWithOrganizationDto[]) {
    employees.value = empList;
  }

  function onChangeDate(selectDate: dayjs.Dayjs) {
    date.value = selectDate;
  }

  async function searchAsync() {
    await loadWithStatusAndErrorHandleAsync(
      (status) => {
        loading.value = status;
      },
      async () => {
        const items: AttendanceItem[] = [];
        const targetDate = date.value.format('YYYY-MM-DD');
        for (const employee of employees.value) {
          items.push(await getAttendanceSearchItemAsync(employee, targetDate));
        }
        searchDate.value = targetDate;
        attendanceItems.value = items;
      }
    );
  }
</script>

<template>
  <DefaultLayout title="근태 조회">
    <EmployeeAutoComplete @on-change="onChangeEmployees" />
    <div class="mt-5">
      <SelectCalendar @on-change="onChangeDate" />
    </div>

    <button
      class="text-lg btn btn-block btn-primary mt-4"
      :disabled="loading || employees.length == 0"
      @click="searchAsync">
      <span
        v-if="loading"
        class="loading loading-spinner" />
      <div v-else>🔍</div>
      조회
    </button>

    <skeleton
      v-if="loading"
      class="my-5" />

    <div v-if="!loading && attendanceItems">
      <div class="mt-6 mb-4 divider text-lg">{{ searchDate }}</div>

      <table class="table text-sm">
        <thead>
          <tr>
            <th>사원</th>
            <th>근무 시작/종료</th>
            <th>상태</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in attendanceItems"
            :key="item.employee.no">
            <td>
              <ul>
                <li class="font-semibold">
                  {{ item.employee.name }}
                </li>
                <li class="text-gray-500">
                  {{ item.employee.organization.name }}
                </li>
              </ul>
            </td>
            <td v-if="item.attendance">
              <template v-if="item.attendance.workStartDt">
                {{ dayjs(item.attendance.workStartDt!).format('HH:mm') }} ~

                <template v-if="item.attendance.workEndDt">
                  {{ dayjs(item.attendance.workEndDt!).format('HH:mm') }}
                </template>
              </template>
            </td>
            <td v-else />
            <td v-if="item.status">
              {{ item.status }}
            </td>
            <td v-else />
          </tr>
        </tbody>
      </table>
    </div>
  </DefaultLayout>
</template>
